import gsap from 'gsap';
import {
    CircleBufferGeometry, Group,
    Mesh
} from 'three';
import { /* SIZE_REFS_MAP, */ SIZE_OPTIONS, SIZE_REFS_MAP } from '../../../constants/sizes';
import { MaterialsFactory } from '../factories/MaterialsFactory';
import { materialsLib } from '../../../../data/settings';
import { ButtonComponent3D } from './ButtonComponent3D';

export class ArrowButton extends ButtonComponent3D {
    #rootG;
    #backMesh;
    #circleMesh;
    #radiusInitial = 1;
    #radius = 1;

    constructor(clickAction) {
        super(clickAction);

        const { arrowButton } = SIZE_REFS_MAP[SIZE_OPTIONS.LANDSCAPE];

        this.#radiusInitial = arrowButton.radius;
        this.#radius = arrowButton.radius;

        const rootG = new Group();
        const geom = new CircleBufferGeometry(
            arrowButton.radius, 36
        );
        const backMesh = new Mesh(geom, MaterialsFactory.create(materialsLib.arrowButtonArrowBasic));
        const circleMesh = new Mesh(geom, MaterialsFactory.create(materialsLib.arrowButtonCircleBasic));

        circleMesh.position.z = -0.02;
        backMesh.position.z = -0.01;

        rootG.scale.set(1.01, 1.01, 1.01);

        rootG.add(backMesh);
        rootG.add(circleMesh);
        this.add(rootG);

        this.#rootG = rootG;
        this.#backMesh = backMesh;
        this.#circleMesh = circleMesh;
        this._mainClickTarget = backMesh;
    }

    _setup() {
        super._setup();
        this.waitForPreload = true;
        this.hasResize = true;
        this.hasStart = true;
        this.clickable = true;
    }

    _resize(winWidth, winHeight, orientation, orientationChanged, changed, pixelRatio) {
        super._resize(winWidth, winHeight, orientation, orientationChanged, changed, pixelRatio);

        if (orientationChanged) {
            const { arrowButton } = SIZE_REFS_MAP[orientation];

            this.#radius = arrowButton.radius;
            const s = arrowButton.radius / this.#radiusInitial;

            this.#rootG.scale.set(s, s, s);
        }
    }

    get radius() {
        return this.#radius;
    }

    start() {
        super.start();
        this._start();
    }

    _start() {
        if (this._tl) this._tl.kill();
        gsap.killTweensOf(this.#circleMesh.scale);
        gsap.killTweensOf(this.#backMesh.scale);

        const ease = 'sine.inOut';
        const duration = 0.6;
        const { rotation } = this.#rootG;
        const { scale } = this.#circleMesh;
        const tl = gsap.timeline({
            repeat: -1,
            delay: duration * 0.6
        });

        tl.addLabel('s', 0);
        tl.fromTo(scale, {
            x: 1,
            y: 1
        }, {
            x: 1.06,
            y: 1.06,
            duration: duration * 0.35,
            ease: 'sine.out'
        });

        tl.fromTo(rotation, {
            y: 0
        }, {
            y: -Math.PI * 2,
            duration: duration * 1.2,
            ease
        }, 's');
        tl.set(rotation, {
            y: 0
        }, `s+=${duration * 1.2}`);

        for (let i = 0; i < 2; i += 1) {
            tl.to(scale, {
                x: 0.95,
                y: 0.95,
                duration,
                ease
            });

            tl.to(scale, {
                x: 1.06,
                y: 1.06,
                duration,
                ease
            });
        }

        tl.to(scale, {
            x: 0.95,
            y: 0.95,
            duration,
            ease
        });

        tl.to(scale, {
            x: 1.16,
            y: 1.16,
            duration: duration * 1.2,
            ease
        });

        tl.to(scale, {
            x: 0.5,
            y: 0.5,
            duration: duration * 0.6,
            ease
        });

        tl.to(scale, {
            x: 1,
            y: 1,
            duration: duration * 0.35,
            ease: 'sine.in'
        });

        this._tl = tl;
    }

    stop() {
        super.stop();

        if (this._tl) this._tl.kill();
        gsap.killTweensOf(this.#circleMesh.scale);
        gsap.killTweensOf(this.#rootG.rotation);
        // gsap.killTweensOf(this.#backMesh.scale);
    }

    setOver() {
        if (!this.visible) return;

        if (this._tl) this._tl.kill();

        gsap.to(this.#circleMesh.scale, {
            x: 1.1,
            y: 1.1,
            duration: 0.45,
            ease: 'elastic.out(1, 0.5)',
            overwrite: 'auto'
        });

        gsap.to(this.#rootG.rotation, {
            y: 0,
            duration: 0.45,
            ease: 'power1.inOut',
            overwrite: 'auto'
        });
    }

    setOut() {
        if (!this.visible) return;

        if (this._tl) this._tl.kill();

        gsap.to(this.#circleMesh.scale, {
            x: 1,
            y: 1,
            duration: 0.35,
            ease: 'elastic.out(1, 0.75)',
            overwrite: 'auto',
            onComplete: () => {
                this._start();
            }
        });
    }
}
